// https://firebase.google.com/docs/auth/web/facebook-login

import { quickLogin } from '@/api/login'
import {
  facebookLogin,
  facebookLoginRedirect,
  getFacebookLoginUserInfo,
} from './facebook'
import {
  googleLogin,
  googleLoginRedirect,
  getGoogleLoginUserInfo,
} from './google'
import { getCookie } from '@/utils/tool'
import { region, getDomain } from '@/utils/utils'
import { globalVariable } from '@/utils/exportVariable'
import VueCookies from 'vue-cookies'
import { getLocation } from '@/utils/ssrCompatible'
import { isInCoinsApp } from '@/utils/utils'
import { autoLogin } from '@/api/login'
import { isMsaWebView } from '@/utils/msa'
import { getAppUserInfo } from '@/utils/native'

// 由于可能存在同一个域名下，同一个token同时存在，但是domain不同的问题，为防止受之前的cookie的影响，重新命名token
export const toplupeToken = 'systemToken'
export const loginTimeKey = 'loginTime'

// 获取登录的用户信息 - 通过后端接口 - 自动登录
export const getLoginUserInfoByApi = async (loginUserInfo) => {
  // 如果已经获取到用户信息了，则直接返回
  if (loginUserInfo) return loginUserInfo
  // 如果没有获取到用户信息，则调用接口获取用户信息
  let userinfo = {}
  try {
    userinfo = await autoLogin()
  } catch (error) {
    // 获取app的用户信息
    userinfo = getAppUserInfo()
  }
  return userinfo || {}
}

/**
 * 登录方法 - 手动登录
 * @param {*} type 登录类型，包含facebook，google，vk
 * @param {*} code vk有cood
 * @param {*} loginUserInfo 登录的用户信息，20221012改变google登录方式，从popup改为重定向
 * @returns
 */
export async function getSignInUserInfo(
  type,
  code,
  useMock = false,
  vkCallbackData = null
) {
  if (useMock) {
    return Promise.reject({
      code: 1208,
    })
  }
  if (!type) return
  // msa通过重定向登录，其他通过弹窗登录
  const isRedirect = isMsaWebView()
  if (isRedirect) {
    loginByRedirect(type)
    return
  }
  const userInfo = await loginByPopup(type)
  return loginByApi(type, code, userInfo, vkCallbackData)
}

// 通过调用API登录
export function loginByApi(type, code, userInfo, vkCallbackData = null) {
  let params = {}
  // vk参数
  if (type === 'vk' && code) {
    params = {
      country: region(),
      code,
      redirectUri: `${window.location.origin}/ru/loginCode`,
      source: 1,
    }
    // 新版 VK ID：添加 codeVerifier 和 deviceId
    if (vkCallbackData) {
      params.codeVerifier = vkCallbackData.codeVerifier
      params.deviceId = vkCallbackData.deviceId
      params.state = vkCallbackData.state
    }
  }

  // facebook和google参数
  if (window.firebase && type !== 'vk') {
    if (!userInfo) return
    const {
      additionalUserInfo: {
        providerId = '',
        profile: { email, picture, name, id },
      },
      credential: { idToken, accessToken },
    } = userInfo || {}
    const providerIdMap = {
      'facebook.com': 2,
      'google.com': 1,
    }
    params = {
      type: providerIdMap[providerId],
      country: region(),
      email,
      userName: name,
      accessToken: providerIdMap[providerId] === 1 ? idToken : accessToken,
      avatarUrl:
        providerIdMap[providerId] === 1
          ? picture
          : picture && picture.data && picture.data.url,
      userId: id,
    }
  }
  return new Promise((resolve, reject) => {
    quickLogin(params, type)
      .then((res) => {
        setLoginCookie(res)
        resolve(res)
      })
      .catch((err) => {
        reject(err)
      })
  })
}

/**
 * 通过弹窗登录
 * @param {*} type google/facebook
 * @returns 用户信息
 */
export async function loginByPopup(type) {
  // 如果没有传参，则不继续执行
  if (!type) return
  // 如果是非谷歌、非Facebook，则不继续执行
  if (['google', 'facebook'].indexOf(type) === -1) return
  const loginMaps = {
    google: googleLogin,
    facebook: facebookLogin,
  }
  let userinfo = {}
  if (loginMaps[type] && typeof loginMaps[type] === 'function') {
    userinfo = await loginMaps[type]()
  }
  return userinfo
}

// 重定向方式登录，无返回值
export function loginByRedirect(type) {
  // 如果没有传参，则不继续执行
  if (!type) return
  // 如果是非谷歌、非Facebook，则不继续执行
  if (['google', 'facebook'].indexOf(type) === -1) return
  const loginMaps = {
    google: googleLoginRedirect,
    facebook: facebookLoginRedirect,
  }
  if (loginMaps[type] && typeof loginMaps[type] === 'function') {
    loginMaps[type]()
  }
}

// 获取重定向方式登录的用户信息
export function getLoginUserInfoByRedirect(type) {
  // 如果是非msa-webview，则直接返回
  return new Promise((resolve, reject) => {
    if (!isMsaWebView()) reject()
    if (!type) reject()
    // 如果是非谷歌、非Facebook，则不继续执行
    if (['google', 'facebook'].indexOf(type) === -1) reject()
    const loginMaps = {
      google: getGoogleLoginUserInfo,
      facebook: getFacebookLoginUserInfo,
    }
    // 获取google或facebook的用户信息
    if (loginMaps[type] && typeof loginMaps[type] === 'function') {
      loginMaps[type]().then((userInfo) => {
        loginByApi(type, '', userInfo)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            reject(err)
          })
      })
    }
  })
}

export function setLoginCookie(res) {
  const { kicoinToken = '', loginTime = '', country = '' } = res || {}
  const domain = getDomain()
  const path = '/'
  delCookie(toplupeToken)
  delCookie(loginTimeKey)
  const kicoinTokenValue = handleKicoinToken({
    kicoinToken,
    country,
    loginTime,
  })
  VueCookies &&
    VueCookies.set(toplupeToken, kicoinTokenValue, '15d', path, domain)
  VueCookies && VueCookies.set(loginTimeKey, loginTime, '15d', path, domain)
}

export function setCookie(cname, cvalue, exdays, domain, path = '/') {
  var d = new Date()
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
  var expires = 'expires=' + d.toGMTString()
  document.cookie =
    cname +
    '=' +
    cvalue +
    '; ' +
    expires +
    ';' +
    'SameSite=Lax' +
    ';' +
    `path=${path}` +
    ';' +
    (domain ? 'domain=' + domain + ';' : '')
}

export function delCookie(name) {
  var cval = getCookie(name)
  if (cval != null) {
    const path = '/'
    const domain = getDomain()
    // 默认domain、path
    setCookie(name, '', -10)
    // 默认domain、指定的path
    setCookie(name, '', -10, getLocation().host, path)
    // 指定的domain，当前路径的path
    setCookie(name, '', -10, domain)
    // 指定的domain、path
    setCookie(name, '', -10, domain, path)
  }
}

// kicoinToken拼接country和登录时间
export function handleKicoinToken(data) {
  const { kicoinToken = '', country = '', loginTime = '' } = data
  if (isInCoinsApp()) return kicoinToken
  return `${kicoinToken}...${country}...${loginTime}`
}

// kicoinToken解析
export function parseKicoinToken(kicoinToken) {
  const tokenList = kicoinToken.split('...')
  return {
    kicoinToken: tokenList[0] || '',
    country: tokenList[1] || '',
    loginTime: tokenList[2] || '',
  }
}

// 获取kicoinToken
export function getMiToken() {
  const token = getCookie(toplupeToken) || ''
  // h5中存储了token、country、logintime，需要解析
  return parseKicoinToken(token)?.kicoinToken || ''
}

// 获取登录时间
export function getLoginTime() {
  if (isInCoinsApp()) return getCookie(loginTimeKey) || ''
  const token = getCookie(toplupeToken) || ''
  return parseKicoinToken(token)?.loginTime || ''
}

// 获取国家 - 仅在h5使用 -app不会调用
export function getCountryByToken() {
  const token = getCookie(toplupeToken) || ''
  return parseKicoinToken(token)?.country || ''
}

// 检查url中的国家合token中的国家是否相同
export function checkoutTokenCountry() {
  if (isInCoinsApp()) return true
  const country = region()
  // 如果当前连接的国家和token的国家不同，则退出登录
  if (country !== getCountryByToken()) {
    globalVariable.store.state.userInfo = null
    return false
  }
  return true
}

export function getLoginStatus() {
  const token = getMiToken()
  const userInfo = globalVariable.store.state.userInfo
  return !!(token && userInfo && userInfo.kicoinToken)
}
